<script lang="ts" setup>
import { z } from 'zod'

const props = defineProps({
  webhook: {
    required: true,
    type: Object as PropType<Webhook>,
  },
})

const removing = ref(false)
const updating = ref(false)
const sending = ref(false)

const format = computed({
  get: () => formatOptions.value.find(option => option.value === form.format),
  set: (option: any) => (form.format = option.value),
})

const formatOptions = computed(() => {
  return [
    {
      icon: getIcon('api'),
      label: t('formats.default'),
      value: 'default',
    },
    {
      icon: getIcon('slack'),
      label: t('formats.slack'),
      value: 'slack',
    },
    {
      icon: getIcon('robot'),
      label: t('formats.zapier'),
      value: 'zapier',
    },
  ]
})

const { createEvent } = useAnalytics()
const { createToast } = useToasts()
const { t } = useI18n()
const teamsStore = useTeamsStore()

const form = reactive({
  format: props.webhook.format || null,
  name: props.webhook.name || '',
  url: props.webhook.url || '',
})

const validation = z.object({
  format: z.enum([
    'default',
    'slack',
    'zapier',
  ]),
  name: z.string()
    .min(1, { message: t('validation.required') })
    .max(255, { message: t('validation.max', { length: 255 }) }),
  url: z.string()
    .url({ message: t('validation.url') })
    .max(255, { message: t('validation.max', { length: 255 }) }),
})

const { isFormUpdated, saveFormState } = useFormState(form)

async function onRemoveWebhook() {
  removing.value = true

  try {
    await teamsStore.removeTeamWebhook({ webhookId: props.webhook.id })

    createEvent({ name: '[Settings] Remove Team Webhook' })
    createToast({ message: 'teamWebhookRemoved' })
  }
  catch (error) {
    console.error(error)
  }

  removing.value = false
}

async function onUpdateWebhook() {
  if (!updating.value && isFormUpdated.value) {
    updating.value = true

    try {
      const updatedWebhook = await teamsStore.updateTeamWebhook({
        format: form.format,
        name: form.name,
        url: form.url,
        webhookId: props.webhook.id,
      })

      // Update format in form if it has been changed by server
      if (updatedWebhook?.format && updatedWebhook.format !== form.format) {
        form.format = updatedWebhook.format
      }

      saveFormState()
      createEvent({ name: '[Settings] Update Team Webhook' })
      createToast({ message: 'teamWebhookUpdated' })
    }
    catch (error) {
      console.error(error)
    }
  }

  updating.value = false
}

async function onSendTestEvent() {
  sending.value = true

  try {
    await teamsStore.sendTeamWebhookTestEvent({ webhookId: props.webhook.id })

    createToast({ message: 'teamWebhookTestEventSent' })
  }
  catch {
    // This error will be handled by Axios interceptor (too many requests).
  }

  sending.value = false
}
</script>

<template>
  <UForm
    :schema="validation"
    :state="form"
    @submit="onUpdateWebhook"
  >
    <li class="flex flex-col gap-4 p-4">
      <div class="flex flex-1 flex-col items-center gap-4 md:flex-row">
        <UFormGroup
          class="w-full flex-1 md:w-3/4"
          name="name"
        >
          <UInput
            v-model="form.name"
            :icon="getIcon('webhook')"
            :placeholder="t('webhookName')"
          />
        </UFormGroup>

        <UFormGroup
          class="w-full md:w-1/4"
          name="name"
        >
          <USelectMenu
            v-model="format"
            :icon="getIcon(`${form.format === 'default' ? 'api' : 'robot'}`)"
            :options="formatOptions"
            @change="onUpdateWebhook"
          />
        </UFormGroup>
      </div>

      <div class="flex items-start">
        <UFormGroup
          class="flex-1"
          name="url"
        >
          <UInput
            v-model="form.url"
            :icon="getIcon('url')"
            :placeholder="t('webhookUrl')"
          />
        </UFormGroup>
      </div>

      <div class="flex items-center">
        <UFormGroup
          class="flex-1"
          name="lists"
        >
          <WebhookAttachListSelector
            class="w-full"
            size="sm"
            :webhook="webhook"
          />
        </UFormGroup>
      </div>

      <div class="flex flex-col items-center gap-4 md:flex-row">
        <UButton
          v-if="isFormUpdated"
          block
          class="mt-0.5 w-full flex-1"
          :icon="getIcon('check')"
          :label="t('globals.update')"
          :loading="updating"
          size="xs"
          type="submit"
          :ui="{ icon: { base: 'text-green-400' } }"
          @click="onUpdateWebhook"
        />

        <UButton
          v-if="!isFormUpdated"
          block
          class="mt-0.5 w-full flex-1"
          :icon="getIcon('plane')"
          :label="t('webhookTestEvent')"
          :loading="sending"
          size="xs"
          :ui="{ icon: { base: 'text-yellow-400' } }"
          @click="onSendTestEvent"
        />

        <UButton
          v-if="!isFormUpdated"
          block
          class="mt-0.5 w-full flex-1"
          :icon="getIcon('trash')"
          :label="t('globals.remove')"
          :loading="removing"
          size="xs"
          :ui="{ icon: { base: 'text-red-600 dark:text-red-400' } }"
          @click="onRemoveWebhook"
        />
      </div>
    </li>
  </UForm>
</template>

<i18n lang="json">
{
  "en": {
    "formats": {
      "default": "Default",
      "slack": "Slack",
      "zapier": "Zapier"
    },
    "webhookName": "Name",
    "webhookTestEvent": "Send a test event",
    "webhookUrl": "URL"
  },
  "es": {
    "formats": {
      "default": "Predeterminado",
      "slack": "Slack",
      "zapier": "Zapier"
    },
    "webhookName": "Nombre",
    "webhookTestEvent": "Enviar un evento de prueba",
    "webhookUrl": "URL"
  },
  "fr": {
    "formats": {
      "default": "Défaut",
      "slack": "Slack",
      "zapier": "Zapier"
    },
    "webhookName": "Nom",
    "webhookTestEvent": "Envoyer un événement de test",
    "webhookUrl": "URL"
  }
}
</i18n>
